import { isEmpty, isEqual } from "lodash";
import { defaultCloudSort, isNtiValue } from "../../../utils";
import { removeSurroundingQuotes } from "../../network/util";
import {
  BulkSearchData,
  ClassificationSearchType,
  CloudSample,
  CloudSearchType,
  LocalSample,
  SearchParams,
  Selections,
  Sort
} from "../@types";
import {
  ACTIVE_FILTER,
  FIRST_COLON_ONLY_REGEX,
  LOCATION,
  NTI_KEYWORDS
} from "../consts";
import { isBasicSearch, shouldSwitchToLocal } from "../util";
import {
  CLOUD_KEYWORDS,
  cloudKeywordsRegex,
  LOCAL_KEYWORDS,
  localKeywordsRegex
} from "./consts";

export const isSearchingUsingLocalKeywords = (query?: string) =>
  localKeywordsRegex.test(query || "");

export const isSearchingUsingCloudKeywords = (query?: string) =>
  cloudKeywordsRegex.test(query || "");

const isNotFoundView = (location: string) =>
  [ACTIVE_FILTER.NOT_FOUND, ACTIVE_FILTER.INVALID].includes(location);

/**
 * Checks if user is using local keywords or selections in search query.
 */
const isUsingLocalKeywordsOrSelections = (
  query = "",
  selections: Selections = {},
  checkForTimeType = true
) => {
  // if there are local keywords in query
  if (isSearchingUsingLocalKeywords(query)) return true;

  // if there are cloud keywords in query
  if (isSearchingUsingCloudKeywords(query)) return false;

  // if there are local only selections
  if (
    LOCAL_KEYWORDS.some(localKeyword =>
      Object.keys(selections || {}).includes(localKeyword)
    )
  )
    return true;

  if (!checkForTimeType) return false;

  return selections.time_type?.value === "submission-time";
};

const isUsingCloudKeywordsOrSelections = (
  query = "",
  selections: Selections = {}
) => {
  // if there are local keywords in query
  if (isSearchingUsingLocalKeywords(query)) return false;

  // if there are cloud keywords in query
  if (isSearchingUsingCloudKeywords(query)) return true;

  // if there are cloud only selections
  return CLOUD_KEYWORDS.some(cloudKeyword =>
    Object.keys(selections || {}).includes(cloudKeyword)
  );
};

/**
 * checks if query should only be sent for local search
 * @param query
 * @param selections
 * @param location
 */
const shouldSendOnlyLocal = (
  query: string,
  selections: Selections,
  location: string
) => !query && isEmpty(selections) && location === LOCATION.LOCAL;

/**
 * check if cloud search is enabled
 *
 * @param query
 * @param selections
 * @param filter
 * @param isBulk
 * @param isCloudDisabled
 */
const isCloudEnabled = ({
  query = "",
  selections = {},
  location = LOCATION.LOCAL,
  isCloudDisabled = false
}: {
  query: string;
  selections: Selections;
  location: string;
  isCloudDisabled: boolean;
}) =>
  !isUsingLocalKeywordsOrSelections(query, selections, false) &&
  !shouldSendOnlyLocal(query, selections, location) &&
  !isCloudDisabled;

/**
 * Checks if query and selections from url params are the same as currently selected selections and current search value
 *
 * @param params
 * @param query
 * @param selections
 */
const getIsSameRequest = (
  params: SearchParams,
  query: string,
  selections: Selections
) => {
  const isSameQuery = (params.q ?? "") === (query ?? "");
  const isSameSelections = isEqual(params.selections || {}, selections || {});
  return isSameQuery && isSameSelections;
};

const createSearchGridKey = (searchParams: SearchParams) => {
  const { location, sort, q, selections } = searchParams;
  const sortString = `${sort.field}_${sort.direction}`;
  const selectionsString = Object.entries(selections || {})
    .map(([key, value]) => `${key}_${value.value}`)
    .join("_");

  return `${location}_${sortString}_${q}_${selectionsString}`;
};

const getSort = (location: string, sort: Sort): Sort => {
  if (location === LOCATION.CLOUD && sort.field === "submission_time") {
    return defaultCloudSort;
  }
  return sort;
};

const filterBulkData = (
  data: Record<string, BulkSearchData>,
  classification: ClassificationSearchType | undefined,
  cloudSearch: CloudSearchType | undefined,
  location: string
) => ({
  ...data,
  [location]: (data[location] ?? [])
    .filter((item: LocalSample | CloudSample) => {
      if (!cloudSearch || isNotFoundView(location)) return true;
      if (cloudSearch === "private") return !item.sample_available;
      return item.sample_available;
    })
    .filter((item: LocalSample | CloudSample) => {
      if (!classification || isNotFoundView(location)) return true;
      return (
        item.threat_status?.toLowerCase() === classification?.toLowerCase()
      );
    })
});

/**
 * Handles switching tab in case there is a local only keyword or selection, or user is currently on
 * not found or invalid hashes bulk search tab. In that case, it switches to Local tab
 */
const switchTabOnSearch = (
  query: string,
  location: string,
  selections?: Selections,
  autoSwitchToCloud?: boolean
) => {
  if (shouldSwitchToLocal(query, selections || {}, location))
    return LOCATION.LOCAL;

  const isLocalKeyword = isUsingLocalKeywordsOrSelections(
    query,
    selections,
    location !== LOCATION.NETWORK
  );
  // submission-user is used on network tab TCB-20507
  if (isLocalKeyword && location !== LOCATION.NETWORK) {
    return LOCATION.LOCAL;
  }

  const isCloudKeyword = isUsingCloudKeywordsOrSelections(query, selections);
  if (isCloudKeyword && autoSwitchToCloud) return LOCATION.CLOUD;

  return [LOCATION.LOCAL, LOCATION.CLOUD, LOCATION.NETWORK].includes(location)
    ? location
    : LOCATION.LOCAL;
};

const isNtiSearchItem = (value: string) => {
  if (value.startsWith("("))
    return NTI_KEYWORDS.some(keyword =>
      value.substring(1).trim().toLowerCase().startsWith(keyword.trim())
    );
  return NTI_KEYWORDS.some(keyword => value.toLowerCase().startsWith(keyword));
};

export const removeBrackets = (value: string): string =>
  value.replace(/^\(|\)$/g, "");

/**
 * Checks whether Nti should be shown.
 * It will not be shown in case that search value is basic search (single value without keyword),
 * there are multiple nti keywords (domain, url or ip)
 * or there is a single nti keyword with multiple values being queried
 */
export const getShowNtiLink = (searchValue: string): boolean => {
  if (!searchValue) return false;

  const isBasic = isBasicSearch(searchValue);
  if (isBasic) return false;

  const ntiSearchItems = searchValue.split(" ").filter(isNtiSearchItem);

  if (ntiSearchItems.length !== 1) return false;

  const [, value] = ntiSearchItems.at(0)?.split(FIRST_COLON_ONLY_REGEX) || [];

  // remove brackets in case that value is wrapped e.g. (domain:google.com)
  const valueWithoutBrackets = removeBrackets(value).trim();
  // remove quotes in case that value is wrapped in quotes
  const unqoutedValue = removeSurroundingQuotes(valueWithoutBrackets);
  return isNtiValue(unqoutedValue);
};

export {
  createSearchGridKey,
  filterBulkData,
  getIsSameRequest,
  getSort,
  isCloudEnabled,
  isNotFoundView,
  isUsingCloudKeywordsOrSelections,
  isUsingLocalKeywordsOrSelections,
  switchTabOnSearch
};
